import { api } from "@/redux/api";

import {
	ListRunEmailSubscription,
	ListRunEmailSubscriptionSchema,
} from "../../pages/List/schema/list-run-email-subscription.schema";

export interface SetListRunEmailSubscriptionArgs {
	listId: number;
	enabled: boolean;
}

/**
 * The signed-in user's run-email subscription for a list (PREC-3908).
 *
 * Its own `injectEndpoints` file because `list.api.ts` is at the file-length ratchet ceiling and
 * may not grow; RTK Query injects into the same base `api` across files
 * (cf. `list-outreach.api.ts`).
 *
 * The setting is personal and read one list at a time on the settings screen, so unlike the list
 * payloads it is not mirrored into a slice — there is no cross-component state to keep in sync.
 */
export const listRunEmailApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getListRunEmailSubscription: builder.query<ListRunEmailSubscription, number>({
			query: (listId) => ({
				url: `list/${listId}/run-email-subscription`,
				responseSchema: ListRunEmailSubscriptionSchema,
			}),
		}),
		setListRunEmailSubscription: builder.mutation<
			ListRunEmailSubscription,
			SetListRunEmailSubscriptionArgs
		>({
			query: ({ listId, enabled }) => ({
				url: `list/${listId}/run-email-subscription`,
				method: "PUT",
				body: { enabled },
				responseSchema: ListRunEmailSubscriptionSchema,
			}),
			// The response is the new setting, so write it straight into this list's cache entry
			// rather than refetching.
			async onQueryStarted({ listId }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;

					dispatch(
						listRunEmailApi.util.updateQueryData("getListRunEmailSubscription", listId, () => data)
					);
				} catch {
					// The save failed, so there is no new setting to write into the cache — the toggle
					// keeps showing the server's last known value. Swallowed because `queryFulfilled`
					// rejects on every failed request, and an uncaught await here becomes an unhandled
					// promise rejection; the mutation's own error state is what the UI reports on.
				}
			},
		}),
	}),
});

export const { useGetListRunEmailSubscriptionQuery, useSetListRunEmailSubscriptionMutation } =
	listRunEmailApi;
